/**
 * B2CRadarSection — landing-page section presenting the new B2C Lead Radar
 * feature (social listening across Reddit, RSS, HN, etc.).
 * Trilingual via the `lang` prop.
 */
import { Radar, MessageSquare, Globe, Gauge, Bot, Crosshair, Clock, Sparkles } from "lucide-react";

const COPY = {
  pt: {
    pill: "Novidade · B2C Lead Radar",
    title: "Encontre leads B2C no momento em que demonstram intenção de compra",
    sub: "O AgenteLead Pro monitora conversas públicas em redes, fóruns e comunidades para identificar consumidores procurando soluções, reclamando de concorrentes ou pedindo recomendações.",
    benefits: [
      { icon: MessageSquare, t: "Detecte intenção real",
        d: "Monitore frases como “alguém recomenda”, “preciso contratar”, “qual o melhor” e “estou procurando” para encontrar oportunidades antes dos concorrentes." },
      { icon: Globe, t: "Múltiplas fontes públicas",
        d: "Reddit, fóruns, blogs, RSS, Hacker News, Google Alerts, F5Bot e mais — todas as fontes permitidas em um só painel." },
      { icon: Gauge, t: "Priorize o que importa",
        d: "B2C Intent Score 0-100 ranqueia automaticamente quais oportunidades merecem atenção imediata." },
      { icon: Bot, t: "Respostas com IA",
        d: "3 estilos consultivos prontos pra revisão humana antes de qualquer publicação. Nada de spam." },
      { icon: Crosshair, t: "Monitore concorrentes",
        d: "Cadastre concorrentes e descubra quando consumidores reclamam ou pedem alternativas." },
      { icon: Clock, t: "Reduza tempo manual",
        d: "Pare de procurar conversa por conversa. O radar centraliza tudo em uma única visão." },
    ],
    howTitle: "Como funciona",
    steps: [
      "Cadastre seu nicho, marca, concorrentes e palavras-chave.",
      "Escolha as fontes que deseja monitorar (Reddit, HN, RSS, Google Alerts…).",
      "O AgenteLead Pro identifica conversas públicas com intenção de compra.",
      "A IA classifica intenção, sentimento e urgência.",
      "Você recebe leads priorizados, alertas em tempo real e respostas sugeridas.",
    ],
    cta1: "Começar a monitorar leads B2C",
    cta2: "Ver como funciona",
  },
  en: {
    pill: "New · B2C Lead Radar",
    title: "Catch B2C leads the moment they show buying intent",
    sub: "AgenteLead Pro monitors public conversations across networks, forums and communities to spot consumers searching for solutions, complaining about competitors or asking for recommendations.",
    benefits: [
      { icon: MessageSquare, t: "Detect real intent",
        d: "Monitor phrases like “anyone recommend”, “need to hire”, “what’s the best” and “looking for” to find opportunities before competitors." },
      { icon: Globe, t: "Multiple public sources",
        d: "Reddit, forums, blogs, RSS, Hacker News, Google Alerts, F5Bot and more — all permitted sources in a single panel." },
      { icon: Gauge, t: "Prioritise what matters",
        d: "B2C Intent Score 0-100 automatically ranks which opportunities deserve immediate attention." },
      { icon: Bot, t: "AI-powered replies",
        d: "3 consultative styles ready for human review before any publishing. Never spammy." },
      { icon: Crosshair, t: "Monitor competitors",
        d: "Register competitors and discover when consumers complain or ask for alternatives." },
      { icon: Clock, t: "Cut manual time",
        d: "Stop tab-hopping across platforms. The radar centralises everything in one view." },
    ],
    howTitle: "How it works",
    steps: [
      "Register your niche, brand, competitors and keywords.",
      "Pick the sources to monitor (Reddit, HN, RSS, Google Alerts…).",
      "AgenteLead Pro identifies public conversations with buying intent.",
      "AI classifies intent, sentiment and urgency.",
      "You get prioritised leads, real-time alerts and suggested replies.",
    ],
    cta1: "Start monitoring B2C leads",
    cta2: "See how it works",
  },
  es: {
    pill: "Nuevo · B2C Lead Radar",
    title: "Encuentra leads B2C cuando muestran intención de compra",
    sub: "AgenteLead Pro monitorea conversaciones públicas en redes, foros y comunidades para detectar consumidores buscando soluciones, quejándose de competidores o pidiendo recomendaciones.",
    benefits: [
      { icon: MessageSquare, t: "Detecta intención real",
        d: "Monitorea frases como “alguien recomienda”, “necesito contratar” y “estoy buscando” para encontrar oportunidades antes que los competidores." },
      { icon: Globe, t: "Múltiples fuentes públicas",
        d: "Reddit, foros, blogs, RSS, Hacker News, Google Alerts, F5Bot — todas las fuentes permitidas en un único panel." },
      { icon: Gauge, t: "Prioriza lo importante",
        d: "B2C Intent Score 0-100 clasifica automáticamente qué oportunidades merecen atención inmediata." },
      { icon: Bot, t: "Respuestas con IA",
        d: "3 estilos consultivos listos para revisión humana antes de publicar. Sin spam." },
      { icon: Crosshair, t: "Monitorea competidores",
        d: "Registra competidores y descubre cuándo los consumidores se quejan o piden alternativas." },
      { icon: Clock, t: "Reduce tiempo manual",
        d: "Deja de buscar conversaciones manualmente. El radar centraliza todo en una vista única." },
    ],
    howTitle: "Cómo funciona",
    steps: [
      "Registra tu nicho, marca, competidores y palabras clave.",
      "Elige las fuentes a monitorear (Reddit, HN, RSS, Google Alerts…).",
      "AgenteLead Pro identifica conversaciones públicas con intención de compra.",
      "La IA clasifica intención, sentimiento y urgencia.",
      "Recibes leads priorizados, alertas en tiempo real y respuestas sugeridas.",
    ],
    cta1: "Empezar a monitorear leads B2C",
    cta2: "Ver cómo funciona",
  },
};

export default function B2CRadarSection({ lang = "pt" }) {
  const c = COPY[lang] || COPY.pt;
  return (
    <section id="b2c-radar" data-testid="landing-b2c-radar"
      className="relative py-24 lg:py-32 overflow-hidden"
      style={{ background: "radial-gradient(ellipse at top, #0F1F40 0%, #0A1233 60%, #050A1F 100%)", color: "#F8FAFC" }}>
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/4 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl"
             style={{ background: "radial-gradient(circle, #84CC16 0%, transparent 60%)" }} />
        <div className="absolute -bottom-40 right-1/4 w-[600px] h-[600px] rounded-full opacity-15 blur-3xl"
             style={{ background: "radial-gradient(circle, #38BDF8 0%, transparent 60%)" }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-300 mb-4">
            <Sparkles className="w-3.5 h-3.5" /> {c.pill}
          </span>
          <h2 className="text-[34px] sm:text-[42px] lg:text-[52px] font-extrabold leading-[1.05] mb-4 text-white">
            {c.title}
          </h2>
          <p className="text-[16.5px] sm:text-[18px] text-slate-300 max-w-2xl mx-auto">
            {c.sub}
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {c.benefits.map((b, i) => (
            <div key={b.t}
              data-testid={`b2c-benefit-${i}`}
              className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 hover:border-emerald-400/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center mb-4 ring-1 ring-emerald-400/30">
                <b.icon className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="font-bold text-[16px] mb-1.5 text-white">{b.t}</h3>
              <p className="text-[13.5px] text-slate-300 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent p-8 lg:p-12 backdrop-blur">
          <div className="flex items-center gap-3 mb-8">
            <Radar className="w-6 h-6 text-emerald-400" />
            <h3 className="text-[24px] sm:text-[28px] font-extrabold text-white">{c.howTitle}</h3>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {c.steps.map((s, i) => (
              <li key={i} data-testid={`b2c-step-${i}`}
                className="relative rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="text-[10.5px] font-mono text-emerald-400 uppercase tracking-widest font-bold mb-2">
                  Passo {String(i + 1).padStart(2, "0")}
                </div>
                <p className="text-[13px] text-slate-200 leading-relaxed">{s}</p>
              </li>
            ))}
          </ol>
        </div>

        {/* CTAs */}
        <div className="text-center mt-10">
          <a href="/login" data-testid="b2c-cta-1"
             className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-[15px] mr-3 mb-3"
             style={{ background: "#84CC16", color: "#0F172A" }}>
            {c.cta1}
          </a>
          <a href="#how" data-testid="b2c-cta-2"
             className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-[15px] border border-white/20 text-white hover:bg-white/5">
            {c.cta2}
          </a>
        </div>

        {/* Compliance footnote */}
        <p className="mt-10 text-center text-[11.5px] text-slate-400 max-w-2xl mx-auto">
          Apenas dados públicos ou autorizados. Não automatizamos publicações. Toda resposta com IA passa por revisão humana.
        </p>
      </div>
    </section>
  );
}
